/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Anuja Gaikwad
 * ReviewQuestions.js
 * Styles for Report question components
 */

import { styled } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';

const PADDING = 16;

export const DialogPaper = styled(Paper)({
  '&&': {
    boxSizing: 'border-box',
    width: '100% !important',
    maxWidth: '800px !important',
    maxHeight: 878,
    borderRadius: 24,
    background: mixins.chalkWhiteFont,
    padding: 48
  }
});

export const DialogTitleStyled = styled(DialogTitle)({
  '&&': {
    padding: 0,
    margin: 0
  }
});

export const Title = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize24,
    lineHeight: mixins.lineHeight32,
    color: mixins.color_text_primary,
    letterSpacing: '-0.48px'
  }
});

export const Question = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontSize: mixins.fontSize18,
    lineHeight: mixins.lineHeight22,
    color: mixins.color_text_primary
  }
});

export const DialogContentStyled = styled(DialogContent)({
  '&&': {
    padding: 0,
    marginTop: 8,
    overflow: 'hidden'
  }
});

export const Container = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  }
});

export const ContentRow = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%'
  }
});

export const CheckboxGroup = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      marginBottom: 8
    }
  }
});

export const CheckboxRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center'
  }
});

export const CustomCheckbox = styled(Checkbox)({
  '&&': {
    padding: 0,
    border: 0,
    marginRight: 12,
    color: mixins.amethyst,
    '&.Mui-checked': {
      color: mixins.amethyst
    },
    '& svg': {
      fontSize: 18
    },
    '&.Mui-disabled svg': {
      fill: mixins.bulkEditCtaButtonDisabledBGColor,
      '& rect': {
        fill: mixins.bulkEditCtaButtonDisabledBGColor
      }
    }
  }
});

export const CheckboxLabel = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.color_text_primary,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight20
  }
});

export const TextArea = styled('textarea')({
  '&&': {
    width: '100%',
    boxSizing: 'border-box',
    height: '120px',
    minHeight: '120px',
    padding: 12,
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.greyScaleColor,
    fontWeight: mixins.fontWeight400,
    lineHeight: mixins.lineHeight24,
    resize: 'vertical',
    '&::placeholder': {
      color: mixins.greyScaleColor,
      opacity: 1
    }
  }
});

export const StudentReportLabel = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.color_text_primary,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight20
  }
});

export const AccordionRoot = styled(Accordion)({
  '&&': {
    position: 'static',
    margin: 0,
    minHeight: 0,
    boxShadow: 'none',
    '&.Mui-expanded': {
      margin: 0
    },
    '&:before': {
      display: 'none'
    }
  }
});

export const AccordionSummaryRoot = styled(AccordionSummary)({
  '&&': {
    flexDirection: 'row-reverse',
    minHeight: 0,
    padding: 0,
    margin: 0,
    '&.Mui-expanded': {
      minHeight: '0 !important'
    },
    '& .MuiAccordionSummary-expandIcon': {
      marginLeft: 32,
      marginRight: 8
    }
  }
});

export const AccordionSummaryContent = styled(Box)({
  '&&': {
    margin: '0 !important',
    '&.Mui-expanded': {
      margin: '0 !important'
    }
  }
});

export const AccordionDetailsRoot = styled(AccordionDetails)({
  '&&': {
    padding: 0,
    display: 'block'
  }
});

export const BadgeTemplateContainer = styled(Box)({
  '&&': {
    display: 'flex',
    gap: 4,
    marginTop: 18,
    marginLeft: 32
  }
});

export const CommentsContainer = styled(Box)({
  '&&': {
    height: '217px',
    marginLeft: '32px',
    padding: PADDING,
    borderRadius: 16,
    background: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.flaggedChipColor}`,
    display: 'flex',
    flexDirection: 'column'
  }
});

export const CommentsTitle = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight16,
    color: mixins.color_text_primary,
    marginBottom: '12px',
    marginTop: '12px'
  }
});

export const CommentsItems = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.color_text_primary,
    marginBottom: 8
  }
});

export const ScrollArea = styled(Box)({
  '&&': {
    flex: 1,
    overflow: 'auto'
  }
});

export const Footer = styled(Box)({
  '&&': {
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 16
  }
});

export const DialogActionsStyled = styled(DialogActions)({
  '&&': {
    margin: 0,
    padding: 0
  }
});

export const Disregards = styled(Button)({
  '&&': {
    height: 40,
    padding: '8px 16px',
    borderRadius: 1000,
    backgroundColor: mixins.chalkWhiteFont,
    border: `2px solid ${mixins.navyColor}`,
    borderColor: mixins.navyColor,
    boxShadow: 'none',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.navyColor,
    minWidth: 124,
    textTransform: 'none'
  },
  '&&:hover': {
    backgroundColor: mixins.secondaryButtonHoverColor,
    borderColor: mixins.navyColor
  }
});

export const ReportInaccurate = styled(Button)({
  '&&': {
    height: 40,
    borderRadius: 1000,
    background: mixins.amethyst,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.chalkWhiteFont,
    boxShadow: 'none',
    textTransform: 'none',
    minWidth: 'unset',
    '&:hover': {
      backgroundColor: `${mixins.amethystColorV2} !important`,
      color: `${mixins.chalkWhiteFont} !important`,
      boxShadow: 'none'
    },
    '&.Mui-disabled': {
      backgroundColor: `${mixins.bulkEditCtaButtonDisabledBGColor} !important`,
      color: `${mixins.bulkEditCtaButtonDisabledColor} !important`,
      boxShadow: 'none',
      opacity: 1
    },
    '&.Mui-disabled:hover': {
      backgroundColor: `${mixins.bulkEditCtaButtonDisabledBGColor} !important`,
      color: `${mixins.bulkEditCtaButtonDisabledColor} !important`
    }
  }
});

export const CloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    top: 16,
    right: 16
  }
});

export const PrimaryCol1 = styled(Box)({
  '&&': {
    padding: '0px 4px'
  }
});
